// client/src/components/tendermaker/RequirementsChipStrip.js
import React, { useState } from "react";

const TYPE_COLORS = {
  requirement: { bg: "#fff", border: "#e5e7eb" },
  annexure: { bg: "#eff6ff", border: "#bfdbfe" },
  unknown: { bg: "#fefce8", border: "#fde68a" },
  custom: { bg: "#faf5ff", border: "#d8b4fe" },
};

export default function RequirementsChipStrip({
  allReqs,
  items,
  activeItemId,
  getItem,
  onSelectRequirement,
  onToggleRemove,
  onAddRequirement,
  onConsolidate,
}) {
  const [filter, setFilter] = useState("");
  const [showRemoved, setShowRemoved] = useState(true);
  const [addMode, setAddMode] = useState(false);
  const [newLabel, setNewLabel] = useState("");

  const removedSet = new Set(
    (items || []).filter((i) => i.removed).map((i) => i.itemId)
  );

  const activeReqs = allReqs.filter((r) => !removedSet.has(r.itemId));
  const removedReqs = allReqs.filter((r) => removedSet.has(r.itemId));

  let displayList = showRemoved ? [...activeReqs, ...removedReqs] : activeReqs;

  if (filter) {
    displayList = displayList.filter((r) =>
      r.label.toLowerCase().includes(filter.toLowerCase())
    );
  }

  const completedCount = activeReqs.filter((r) => {
    const it = getItem(r.itemId);
    return it?.sourceType === "draft" || Boolean(it?.sourceRef?.key) || (it?.files?.length > 0);
  }).length;

  return (
    <div
      style={{
        border: "1px solid #e5e7eb",
        borderRadius: 10,
        padding: "6px 8px",
        background: "#fff",
      }}
    >
      {/* Header: progress + filter + add + consolidate */}
      <div style={{ display: "flex", alignItems: "center", gap: 5, marginBottom: 4, flexWrap: "wrap" }}>
        <span style={{ fontWeight: 800, fontSize: 11, color: "#111827" }}>Reqs</span>
        <span style={{ color: "#6b7280", fontSize: 9, fontWeight: 600 }}>
          {completedCount}/{activeReqs.length}
        </span>
        {/* Progress bar */}
        <div style={{ width: 40, height: 3, borderRadius: 2, background: "#e5e7eb", overflow: "hidden" }}>
          <div
            style={{
              width: activeReqs.length > 0 ? `${(completedCount / activeReqs.length) * 100}%` : "0%",
              height: "100%",
              background: "#16a34a",
              borderRadius: 2,
            }}
          />
        </div>

        <input
          type="text"
          placeholder="Filter..."
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          style={{ padding: "2px 6px", borderRadius: 6, border: "1px solid #e5e7eb", fontSize: 10, width: 70, outline: "none" }}
        />

        {addMode ? (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (newLabel.trim()) {
                onAddRequirement(newLabel.trim());
                setNewLabel("");
                setAddMode(false);
              }
            }}
            style={{ display: "flex", gap: 3, alignItems: "center" }}
          >
            <input
              autoFocus
              type="text"
              placeholder="Name..."
              value={newLabel}
              onChange={(e) => setNewLabel(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Escape") { setAddMode(false); setNewLabel(""); } }}
              style={{ padding: "2px 6px", borderRadius: 6, border: "1px solid #d8b4fe", fontSize: 10, width: 100, outline: "none" }}
            />
            <button
              type="submit"
              disabled={!newLabel.trim()}
              style={{
                padding: "1px 6px", borderRadius: 6, border: "1px solid #d8b4fe",
                background: newLabel.trim() ? "#7c3aed" : "#f9fafb",
                color: newLabel.trim() ? "#fff" : "#6b7280",
                cursor: newLabel.trim() ? "pointer" : "default", fontWeight: 700, fontSize: 9,
              }}
            >
              Add
            </button>
            <button
              type="button"
              onClick={() => { setAddMode(false); setNewLabel(""); }}
              style={{ padding: "1px 5px", borderRadius: 6, border: "1px solid #e5e7eb", background: "#f9fafb", cursor: "pointer", fontWeight: 700, fontSize: 9, color: "#6b7280" }}
            >
              Cancel
            </button>
          </form>
        ) : (
          <button
            onClick={() => setAddMode(true)}
            style={{ padding: "1px 6px", borderRadius: 6, border: "1px solid #d8b4fe", background: "#faf5ff", cursor: "pointer", fontWeight: 700, fontSize: 9, color: "#7c3aed" }}
          >
            + Add
          </button>
        )}

        {removedReqs.length > 0 && (
          <button
            onClick={() => setShowRemoved((v) => !v)}
            style={{ padding: "1px 6px", borderRadius: 6, border: "1px solid #e5e7eb", background: showRemoved ? "#fef2f2" : "#f9fafb", cursor: "pointer", fontWeight: 700, fontSize: 9, color: "#6b7280" }}
          >
            {showRemoved ? "Hide removed" : `+${removedReqs.length}`}
          </button>
        )}

        {onConsolidate && (
          <button
            onClick={onConsolidate}
            style={{ padding: "1px 6px", borderRadius: 6, border: "1px solid #e5e7eb", background: "#f0fdf4", cursor: "pointer", fontWeight: 700, fontSize: 9, color: "#166534", marginLeft: "auto" }}
          >
            Consolidate
          </button>
        )}
      </div>

      {/* Scrollable chip area */}
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: 3,
          maxHeight: 80,
          overflowY: "auto",
          overflowX: "hidden",
        }}
      >
        {displayList.map((r) => {
          const it = getItem(r.itemId);
          const isActive = activeItemId === r.itemId;
          const isRemoved = removedSet.has(r.itemId);
          const hasDraft = it?.sourceType === "draft";
          const hasFile = Boolean(it?.sourceRef?.key) || (it?.files?.length > 0);
          const colors = TYPE_COLORS[r.type] || TYPE_COLORS.requirement;

          return (
            <span key={r.itemId} style={{ display: "inline-flex", alignItems: "center", position: "relative" }}>
              <button
                onClick={() => { if (!isRemoved) onSelectRequirement(r.itemId); }}
                title={isRemoved ? `${r.label} (removed)` : r.label}
                style={{
                  padding: "3px 7px",
                  paddingRight: 18,
                  borderRadius: 999,
                  border: isActive ? "2px solid #2563eb" : isRemoved ? "1px dashed #d1d5db" : `1px solid ${colors.border}`,
                  background: isActive ? "#dbeafe" : isRemoved ? "#f9fafb" : (hasDraft || hasFile) ? "#f0fdf4" : colors.bg,
                  cursor: isRemoved ? "default" : "pointer",
                  fontWeight: isActive ? 900 : 600,
                  fontSize: 10,
                  lineHeight: 1.2,
                  maxWidth: 160,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 3,
                  opacity: isRemoved ? 0.5 : 1,
                  textDecoration: isRemoved ? "line-through" : "none",
                  color: isRemoved ? "#9ca3af" : "#111827",
                }}
              >
                {!isRemoved && (hasDraft || hasFile) && (
                  <span style={{ width: 5, height: 5, borderRadius: "50%", background: hasDraft ? "#16a34a" : "#3b82f6", flexShrink: 0 }} />
                )}
                {r.label.length > 20 ? r.label.slice(0, 18) + ".." : r.label}
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); onToggleRemove(r.itemId); }}
                title={isRemoved ? "Restore" : "Remove"}
                style={{
                  position: "absolute", right: 2, top: "50%", transform: "translateY(-50%)",
                  width: 14, height: 14, borderRadius: "50%", border: "none",
                  background: isRemoved ? "#dcfce7" : "transparent",
                  cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: 9, fontWeight: 900, color: isRemoved ? "#16a34a" : "#9ca3af",
                  padding: 0, lineHeight: 1, opacity: isRemoved ? 1 : 0.5,
                }}
                onMouseEnter={(e) => { e.currentTarget.style.opacity = "1"; e.currentTarget.style.background = isRemoved ? "#bbf7d0" : "#fee2e2"; e.currentTarget.style.color = isRemoved ? "#16a34a" : "#dc2626"; }}
                onMouseLeave={(e) => { e.currentTarget.style.opacity = isRemoved ? "1" : "0.5"; e.currentTarget.style.background = isRemoved ? "#dcfce7" : "transparent"; e.currentTarget.style.color = isRemoved ? "#16a34a" : "#9ca3af"; }}
              >
                {isRemoved ? "+" : "\u00d7"}
              </button>
            </span>
          );
        })}
      </div>
    </div>
  );
}
